import MuiContainer from "@mui/material/Container";
import { useContext } from "react";
import { PageHelpLineContext } from "../internal/page-help-line";
import { Typography } from "./Typography";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ContainerLayout, ContainerProps } from "../types";

/**
 * Containment note (types.ts): `as` fixes the upstream `RootComponent`
 * generic to a closed union (`'div' | 'main' | 'section'`); `AdditionalProps`
 * is fixed to `{}` — no other upstream-generic behaviour is forwarded.
 *
 * Page-ground overrides live on `PortalLayout`'s `background` prop, which
 * paints `main` itself (DEBT-20261015-fzd5), not here.
 *
 * ADR-20261016-v6nd (AC-2's "760px content width"): every caller in the
 * tree uses the DEFAULT `maxWidth` (no caller anywhere passes "sm", "md",
 * "xl" or `false` — grepped at the time of this fix), which previously
 * forwarded `maxWidth="lg"` straight to Material UI's own `Container`.
 * That resolves against `theme.breakpoints.values.lg` (1200, tokens.ts)
 * and, by Material UI's own `Container` implementation, applies only
 * ABOVE that breakpoint (`theme.breakpoints.up("lg")`) -- so every portal
 * screen rendered at an ordinary desktop width with no max-width cap at
 * all, nothing close to H2's actual, deliberate 760px content width
 * (`DESIGN-SYSTEM-DEPARTURES.md`'s own "Other known departures": "Content
 * width is 760px (696px inner measure), not the design system's
 * `contentMax` of 720px" -- `tokens.layout.contentMax` was itself unused
 * anywhere until this fix). Passing `maxWidth={false}` to Material UI's
 * `Container` disables ITS OWN breakpoint-gated cap entirely (gutters are
 * independent of `maxWidth` and stay applied); the `sx` below then applies
 * `contentMax` unconditionally, at every viewport width, centred with
 * `marginInline: "auto"`, which a breakpoint-keyed `maxWidth` prop cannot
 * do. A future caller that explicitly asks for "sm"/"md"/"xl"/`false`
 * still gets Material UI's own ordinary behaviour for that value,
 * untouched.
 *
 * ADR-20261016-v6nd: the H2 handoff README's own "Global shell" section
 * names the content column's left-right padding explicitly -- 32px
 * desktop / 16px mobile -- which Material UI's own default `Container`
 * gutters (8px/12px) never matched. Set here to the token values that
 * equal H2's own px figures (`spacing.xl`=32, `spacing.md`=16). The
 * column's own top/bottom padding is deliberately NOT set here: `Container`
 * is reused for more than "the page's own top-level content" (e.g.
 * `PortalLayout`'s own help-line wrapper, which already supplies its own
 * top spacing) -- adding a column-level top padding here would double up
 * on callers that are not that one, unscoped churn this fix does not
 * attempt.
 */
const sp = tokens.spacing;

/** H2's own column for each screen, value for value from the screen files. */
const LAYOUT_SX: Record<ContainerLayout, SxProps<Theme>> = {
  entry: {
    padding: "clamp(24px, 6vw, 56px) clamp(16px, 4vw, 32px) clamp(48px, 8vw, 88px)",
  },
  summary: {
    maxWidth: `${tokens.layout.contentMax}px`,
    marginInline: "auto",
    padding: "clamp(24px, 4vw, 40px) clamp(16px, 4vw, 32px) 64px",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(20px, 3vw, 32px)",
  },
  flow: {
    maxWidth: `${tokens.layout.contentMax}px`,
    marginInline: "auto",
    padding: { xs: `20px ${sp.md}px ${sp.xxl}px`, sm: `${sp.xl}px ${sp.xl}px ${sp.xxxl}px` },
    display: "flex",
    flexDirection: "column",
    gap: { xs: `${sp.md}px`, sm: `${sp.lg}px` },
  },
  exposures: {
    maxWidth: `${tokens.layout.contentMax}px`,
    marginInline: "auto",
    padding: { xs: `${sp.lg}px ${sp.md}px ${sp.xxl}px`, sm: `${sp.xl}px ${sp.xl}px ${sp.xxl}px` },
    display: "flex",
    flexDirection: "column",
    gap: { xs: `${sp.md}px`, sm: `${sp.lg}px` },
  },
};

export function Container({ as, maxWidth = "lg", disableGutters, id, children, testId, layout }: Readonly<ContainerProps>) {
  const helpLine = useContext(PageHelpLineContext);
  const help =
    helpLine === undefined || helpLine === null ? null : (
      <Typography variant="bodySmall" tone="muted">
        {helpLine}
      </Typography>
    );
  if (layout !== undefined) {
    return (
      <MuiContainer
        maxWidth={false}
        disableGutters
        {...cleanProps({ component: as, id, "data-testid": testId, sx: { ...LAYOUT_SX[layout], width: "100%" } })}
      >
        {layout === "entry" ? (
          <Box sx={{ maxWidth: 696, marginInline: "auto", display: "flex", flexDirection: "column", gap: `${sp.lg}px` }}>
            {children}
            {help}
          </Box>
        ) : (
          <>
            {children}
            {help}
          </>
        )}
      </MuiContainer>
    );
  }
  const usesPortalContentWidth = maxWidth === "lg";
  return (
    <MuiContainer
      maxWidth={usesPortalContentWidth ? false : maxWidth}
      {...cleanProps({
        component: as,
        disableGutters,
        id,
        "data-testid": testId,
        sx: usesPortalContentWidth
          ? {
              maxWidth: `${tokens.layout.contentMax}px`,
              marginInline: "auto",
              paddingInline: { xs: `${tokens.spacing.md}px`, sm: `${tokens.spacing.xl}px` },
              paddingTop: { xs: `${tokens.spacing.lg}px`, sm: `${tokens.spacing.xl}px` },
              paddingBottom: `${tokens.spacing.xxl}px`,
            }
          : undefined,
      })}
    >
      {children}
      {help === null ? null : <Box sx={{ paddingTop: `${tokens.spacing.lg}px` }}>{help}</Box>}
    </MuiContainer>
  );
}
